import { useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api, cents, day, month, points, rate, useCurrentCompany, useMe } from '../../lib'
import { ErrorBox, Loading, NoCompany } from '../../app/ui'
import { CardEntry } from '../card/CardPages'
import { StatusBadge } from './parts'
import { ProviderLogo } from './logos'
import { SkyCard } from '../../app/SkyCard'
import type { Overview } from './types'

const ACTIVITY_LABEL: Record<string, string> = {
  award: 'Points earned',
  'award.posted': 'Points earned',
  award_reversal: 'Points taken back (refund)',
  release: 'Points ready',
  reserve: 'Points held for your payout',
  unreserve: 'Points back',
  settle: 'Payout paid',
  'redeem.reserve': 'Points held for your payout',
  'redeem.unreserve': 'Points back',
  'redeem.settle': 'Payout paid',
  'travel.reserve': 'Points held for a trip',
  'travel.unreserve': 'Points back',
  'travel.settle': 'Trip booked',
  'travel.unsettle': 'Points back (trip refund)',
  fix: 'Correction',
}

// Payout states in a few words (redeem's statuses).
const PAYOUT_STATUS: Record<string, string> = {
  pending_approval: 'Being checked',
  needs_bank: 'Needs your bank',
  approved: 'Approved',
  approved_manual: 'Approved',
  on_hold: 'On hold',
  sending: 'Sending',
  submitted: 'Sending',
}

// Rows that move points between your own buckets (net 0): say where they went.
const MOVED_TO: Record<string, string> = {
  release: 'now ready',
  'redeem.reserve': 'held',
  reserve: 'held',
  'redeem.unreserve': 'back to ready',
  'travel.reserve': 'held',
  'travel.unreserve': 'back to ready',
  unreserve: 'back to ready',
}

// Ledger memos are written for staff ("1000 points for 2026-10"); show our label and the month instead.
function activityLabel(kind: string, memo: string | null): string {
  const label = ACTIVITY_LABEL[kind]
  if (!label) return memo || kind.replace(/[._]/g, ' ')
  const m = memo?.match(/for (\d{4}-\d{2})\b/)
  return m ? `${label} for ${month(`${m[1]}-01`)}` : label
}

export function OverviewPage() {
  const { me } = useMe()
  const company = useCurrentCompany()
  const [cursor, setCursor] = useState<string | null>(null)
  const q = useQuery({
    queryKey: ['web', 'overview', company?.id, cursor],
    queryFn: () =>
      api<Overview>('GET', `/api/companies/${company!.id}/overview${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ''}`),
    enabled: !!company,
  })

  if (me && !company) return <NoCompany />
  if (q.isLoading || !q.data) return q.error ? <ErrorBox error={q.error} /> : <Loading />
  const o = q.data
  const fresh = !cursor && o.ready.points === 0 && o.awaiting_total_points === 0 && o.debt_points === 0 &&
    o.payouts_in_progress.length === 0 && o.partners.length === 0 && o.found.length === 0 && o.activity.rows.length === 0
  if (fresh) return <FirstRun companyId={o.company.id} name={o.company.name} />

  return (
    <div className="e-col e-overview" data-testid="web-overview">
      <StageBand name={o.company.name}>
        <div className="e-band-hero" data-testid="web-ready-panel">
          <h1 className="e-band-label">{o.company.name}</h1>
          <p className="e-band-figure"><span className="num" data-testid="web-ready-points">{points(o.ready.points)}</span> <span className="e-band-unit">points ready</span></p>
          <p className="e-band-sub">
            Worth <span className="num" data-testid="web-ready-usd">{cents(o.ready.cents)}</span> back on charges from ernd partners.{' '}
            <span className="num" data-testid="web-awaiting-points">{points(o.awaiting_total_points)}</span> points on the way.
            {o.awaiting_total_points > 0 ? ' Receipt points count once the receipt is verified, and partner points once the partner pays.' : ''}
          </p>
          {o.debt_points > 0 && (
            <p className="e-band-sub" data-testid="web-debt">
              <strong className="num" data-testid="web-debt-points">{points(o.debt_points)}</strong> points owed. Future points pay this down first.
            </p>
          )}
          <div className="e-band-actions">
            {o.can_redeem ? (
              <Link className="e-btn e-btn-primary" to="/redeem" data-testid="web-redeem">Redemption settings</Link>
            ) : (
              <p className="e-band-sub" data-testid="web-ask-admin">Ask a company admin to choose partners.</p>
            )}
            <Link className="e-band-link" to="/invoices">See receipts</Link>
          </div>
          <p className="e-band-fine">100 points = $1. Points never pay OpenAI or Anthropic charges.</p>
        </div>
      </StageBand>
      <CardEntry companyId={o.company.id} />
      {(o.would_have_earned ?? []).length > 0 ? (
        <section className="e-panel e-would">
          {(o.would_have_earned ?? []).map((w) => (
            <p key={w.partner_id} data-testid={`web-would-${w.slug ?? w.partner_id}`}>
              Before you joined, your {w.name} spend would have earned <span className="num">{points(w.points)}</span> points.
            </p>
          ))}
        </section>
      ) : null}

      {o.awaiting_total_points > 0 || o.awaiting.length > 0 ? (
        <section className="e-panel e-rows" data-testid="web-awaiting-panel">
          <div className="e-rows-head">
            <h2 className="e-heading">On the way</h2>
            <span className="e-rows-total num">{points(o.awaiting_total_points)} points</span>
          </div>
          <ul>
            {o.awaiting.map((a) => (
              <li key={a.partner_id} data-testid={`web-awaiting-${a.slug ?? a.partner_id}`}>
                <ProviderLogo slug={a.slug ?? ''} name={a.name} size={32} />
                <Link className="e-row-name" to={`/directory/${a.partner_id}`}>{a.name}</Link>
                <span className="num">{points(a.points)}</span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {o.payouts_in_progress.length > 0 ? (
        <section className="e-panel e-rows" data-testid="web-payouts-panel">
          <h2 className="e-heading">Cash on its way to you</h2>
          <ul>
            {o.payouts_in_progress.map((r) => (
              <li key={r.id} data-testid="web-payout-row">
                <span className="e-row-name">{r.partner_name}<span className="e-row-note">{r.method === 'credit' ? 'Bill credit' : 'Cash back'}, {(PAYOUT_STATUS[r.status] ?? r.status.replace(/_/g, ' ')).toLowerCase()}</span></span>
                <span className="num">{cents(r.amount_cents)}</span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <section className="e-panel e-rows" data-testid="web-partners-panel">
        <div className="e-rows-head">
          <h2 className="e-heading">Your partners</h2>
          <Link className="e-link" to="/directory">Find more partners</Link>
        </div>
        {o.partners.length === 0 ? (
          <p className="e-rows-empty">None yet. A partner links on its own when someone on your team signs in with the email that partner bills.</p>
        ) : (
          <ul>
            {o.partners.map((p) => (
              <li key={p.partner_id} data-testid={`web-your-partner-${p.slug ?? p.partner_id}`}>
                <ProviderLogo slug={p.slug ?? ''} name={p.name} size={32} />
                <span className="e-row-name">
                  <Link to={`/directory/${p.partner_id}`}>{p.name}</Link>
                  <span className="e-row-note">{rate(p.rate)}{p.points_awaiting_funding ? `, ${points(p.points_awaiting_funding)} on the way` : ''}</span>
                </span>
                <StatusBadge status={p.status} />
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* "Partners we found for you" (o.found) were domain matches, which can't link anything (DECISIONS 12, 22): not shown. */}
      {o.can_redeem && (
        <section className="e-panel e-invite">
          <div>
            <h2 className="e-heading">Invite your team</h2>
            <p className="e-row-note">Members see points. Admins choose where they pay back.</p>
          </div>
          <Link className="e-btn" to="/team" data-testid="web-invite-team">Invite</Link>
        </section>
      )}

      <section className="e-panel e-rows" data-testid="web-activity-panel">
        <h2 className="e-heading">Activity</h2>
        {o.activity.rows.length === 0 ? (
          <p className="e-rows-empty">No activity yet.</p>
        ) : (
          <ul>
            {o.activity.rows.map((a) => (
              <li key={a.journal_id}>
                <span className="e-row-name">{activityLabel(a.kind, a.memo)}<span className="e-row-note">{day(a.at)}</span></span>
                {a.points_delta === 0 && a.points_moved ? (
                  <span className="num e-row-quiet" data-testid="web-activity-moved">{points(a.points_moved)} {MOVED_TO[a.kind] ?? 'moved'}</span>
                ) : (
                  <span className={`num ${a.points_delta < 0 ? 'negative' : ''}`}>{a.points_delta > 0 ? '+' : ''}{points(a.points_delta)}</span>
                )}
              </li>
            ))}
          </ul>
        )}
        {cursor || o.activity.next_cursor ? (
          <div className="e-rows-pager">
            {cursor && <button type="button" className="e-btn" onClick={() => setCursor(null)}>Newest</button>}
            {o.activity.next_cursor && <button type="button" className="e-btn" onClick={() => setCursor(o.activity.next_cursor)}>Older</button>}
          </div>
        ) : null}
      </section>
    </div>
  )
}

type Setup = {
  link_requests: { id: string; partner_name: string }[]
  invoices: { counted: number; needs_review: number; rejected: number; duplicate: number }
}

/** A company with nothing yet: one next step instead of a dashboard of empty panels. */
function FirstRun({ companyId, name }: { companyId: string; name: string }) {
  const setup = useQuery({
    queryKey: ['web', 'overview', companyId, 'setup'],
    queryFn: () => api<Setup>('GET', `/api/companies/${companyId}/onboarding`),
    refetchInterval: 10_000,
  })
  const inboxes = useQuery({
    queryKey: ['inboxes', companyId],
    queryFn: () => api<{ rows: { mailbox: string; status: string }[] }>('GET', `/api/companies/${companyId}/inboxes`),
    refetchInterval: 10_000,
  })
  const s = setup.data
  const checking = (s?.invoices.needs_review ?? 0) + (s?.invoices.counted ?? 0)
  const waiting = s?.link_requests.map((r) => r.partner_name) ?? []
  const inbox = inboxes.data?.rows.find((r) => r.status === 'active')?.mailbox
  return (
    // Still the overview (data-testid web-overview); a brand-new company sees one first step instead of zeros.
    <div className="e-col e-first" data-testid="web-overview">
      <div className="e-first-inner" data-testid="web-first-run">
      <StageBand name={name} />
      <section className="e-panel e-first-step" data-testid="web-first-step">
        {inbox ? (
          <div className="e-first-marks" aria-hidden="true">
            <ProviderLogo slug="openai" size={44} />
            <ProviderLogo slug="anthropic" size={44} />
          </div>
        ) : null}
        <div className="e-first-text">
          {/* Founder, 2026-10-24: once an inbox is connected and no receipt has counted yet, say so. When one counts,
              the company has points and the normal overview replaces this panel. */}
          <h2 className="e-stat" data-testid="web-first-heading">{inbox ? 'Waiting for your first receipt' : 'Send us your AI receipts'}</h2>
          {inbox ? (
            <p className="e-first-main" data-testid="web-first-inbox">
              {inbox}. New OpenAI and Anthropic receipts there count as they arrive.
            </p>
          ) : null}
          {checking > 0 ? (
            <p className="e-first-main" data-testid="web-first-checking">
              {checking === 1 ? '1 receipt is' : `${checking} receipts are`} being checked. Points show here once they count.
            </p>
          ) : null}
          <p className="e-first-rates">
            OpenAI and Anthropic earn 4 points per $1, including last month's spend. Superserve earns 5 from the day you joined. 100 points is $1.
          </p>
        </div>
        <div className="e-first-actions">
          {inbox ? (
            <Link className="e-btn e-btn-primary" to="/invoices">See receipts</Link>
          ) : (
            <Link className="e-btn e-btn-primary" to="/invoices">Connect your inbox or upload receipts</Link>
          )}
        </div>
      </section>
      <CardEntry companyId={companyId} />
      {waiting.length > 0 ? (
        <p className="e-first-waiting" data-testid="web-first-waiting">
          Linking your {waiting.join(' and ')} account{waiting.length > 1 ? 's' : ''}.
        </p>
      ) : null}
      </div>
    </div>
  )
}

/** The top of the overview: the company's name on the dark stage, with the card. */
function StageBand({ name, children }: { name: string; children?: ReactNode }) {
  return (
    <header className="e-band e-stage">
      <div className="e-band-inner">
        <SkyCard className="e-band-card" />
        {children ?? <h1 className={`e-title e-band-name${name.length > 24 ? ' e-band-name-long' : ''}`} title={name}>{name}</h1>}
      </div>
    </header>
  )
}
